import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { FormMessage } from "@/components/ui/form-message";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AuthActionError,
  authErrorToTranslationKey,
  confirmCurrentPasswordAndUpdatePassword,
} from "@/features/auth/api/authClient";
import {
  passwordChangeSchema,
  type PasswordChangeFormValues,
} from "@/features/profile/validation/profileSchemas";
import { useAuthStore } from "@/stores/authStore";

export function PasswordChangeForm() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const [message, setMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<PasswordChangeFormValues>({
    resolver: zodResolver(passwordChangeSchema),
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
  });

  async function onSubmit(values: PasswordChangeFormValues) {
    setMessage(null);
    setSubmitError(null);

    try {
      if (!user?.email) {
        throw new AuthActionError("generic");
      }

      await confirmCurrentPasswordAndUpdatePassword({
        email: user.email,
        currentPassword: values.current_password,
        newPassword: values.new_password,
      });

      form.reset();
      setMessage(t("profile.passwordSuccess"));
    } catch (error) {
      if (error instanceof AuthActionError) {
        setSubmitError(t(authErrorToTranslationKey(error.code)));
        return;
      }

      setSubmitError(t("auth.errors.generic"));
    }
  }

  return (
    <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
      <div>
        <Label htmlFor="current_password">{t("auth.fields.currentPassword")}</Label>
        <Input
          id="current_password"
          type="password"
          autoComplete="current-password"
          {...form.register("current_password")}
        />
        {form.formState.errors.current_password ? (
          <FormMessage>
            {t(form.formState.errors.current_password.message ?? "auth.errors.generic")}
          </FormMessage>
        ) : null}
      </div>

      <div>
        <Label htmlFor="new_password">{t("auth.fields.newPassword")}</Label>
        <Input
          id="new_password"
          type="password"
          autoComplete="new-password"
          {...form.register("new_password")}
        />
        {form.formState.errors.new_password ? (
          <FormMessage>
            {t(form.formState.errors.new_password.message ?? "auth.errors.generic")}
          </FormMessage>
        ) : null}
      </div>

      <div>
        <Label htmlFor="confirm_password">{t("auth.fields.confirmPassword")}</Label>
        <Input
          id="confirm_password"
          type="password"
          autoComplete="new-password"
          {...form.register("confirm_password")}
        />
        {form.formState.errors.confirm_password ? (
          <FormMessage>
            {t(form.formState.errors.confirm_password.message ?? "auth.errors.generic")}
          </FormMessage>
        ) : null}
      </div>

      {message ? <FormMessage className="text-olive">{message}</FormMessage> : null}
      {submitError ? <FormMessage>{submitError}</FormMessage> : null}

      <Button type="submit" disabled={form.formState.isSubmitting}>
        {form.formState.isSubmitting ? t("common.saving") : t("profile.securityTitle")}
      </Button>
    </form>
  );
}
